import React from 'react';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import {
  Box,
  Checkbox,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import type { AutoMergeCandidate } from '../../types';

interface AutoMergeCandidateCardProps {
  candidate: AutoMergeCandidate;
  isSelected: boolean;
  onToggle: () => void;
  index: number;
}

export const AutoMergeCandidateCard: React.FC<AutoMergeCandidateCardProps> = ({
  candidate,
  isSelected,
  onToggle,
  index,
}) => {
  const theme = useTheme();
  const overrideEntries = Object.entries(candidate.demographic_overrides);

  return (
    <Paper
      sx={{
        mb: 2,
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        opacity: isSelected ? 1 : 0.5,
        transition: 'opacity 0.15s',
      }}
    >
      {/* Card Header */}
      <Box
        sx={{
          p: 1.5,
          px: 2,
          backgroundColor: alpha(theme.palette.primary.main, 0.05),
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Checkbox
          checked={isSelected}
          onChange={onToggle}
          color="primary"
         slotProps={{ input: { 'aria-label': `Include group ${index + 1} in auto-merge` } }}/>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Group {index + 1}
        </Typography>
        {candidate.match_reasons.map(reason => (
          <Chip
            key={reason}
            label={reason.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ fontWeight: 500 }}
          />
        ))}
        <Chip
          label={`${candidate.confidence.toFixed(0)}% match`}
          size="small"
          color="success"
          sx={{ fontWeight: 500, ml: 'auto' }}
        />
      </Box>

      {/* Patient Table */}
      <TableContainer>
        <Table size="small" aria-label={`Auto-merge group ${index + 1}`}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>First Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Last Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>DOB</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Insurance</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {candidate.patients.map((patient, patientIndex) => {
              const isPrimary = patient.patient_id === candidate.suggested_primary_id;
              const rowBg = patientIndex % 2 === 0 ? 'transparent' : alpha(theme.palette.grey[500], 0.03);

              return (
                <TableRow
                  key={patient.patient_id}
                  sx={{
                    backgroundColor: isPrimary
                      ? alpha(theme.palette.primary.main, 0.06)
                      : rowBg,
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {patient.first_name || '—'}
                      {isPrimary && (
                        <Chip label="Primary" size="small" color="primary" sx={{ height: 20, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{patient.last_name || '—'}</TableCell>
                  <TableCell>
                    {patient.date_of_birth
                      ? new Date(patient.date_of_birth).toLocaleDateString()
                      : '—'}
                  </TableCell>
                  <TableCell>{patient.email || '—'}</TableCell>
                  <TableCell>{patient.mobile_phone || '—'}</TableCell>
                  <TableCell>{patient.insurance_provider || '—'}</TableCell>
                  <TableCell>{new Date(patient.created_at).toLocaleDateString()}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Demographic Overrides */}
      {overrideEntries.length > 0 && (
        <Box
          sx={{
            px: 2,
            py: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: alpha(theme.palette.info.main, 0.04),
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
            Will fill on primary:
          </Typography>
          {overrideEntries.map(([key, value]) => (
            <Chip
              key={key}
              icon={key === 'email' ? <EmailIcon /> : <PhoneIcon />}
              label={`${key}: ${value}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
          ))}
        </Box>
      )}
    </Paper>
  );
};
